import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, includes, orderBy } from "lodash";
import numeral from "numeral";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StatusChip from "../../../common/js/components/StatusChip";
import {
    Add,
    DownChevron,
    LeftChevron,
    Sorty,
    StepperIcon,
    StepperIconComplete,
    UpChevron,
} from "../../../common/js/icons";
import DocumentDrop from "../documents/DocumentDropContainer";
import utils from "../utils/index";
import {
    aggregateBrokers,
    booleanToYN,
    buildAddress,
    isBrokerOnDeal,
    isBrokerOnListing,
    isPropertyInState,
    minMaxSqftConcat,
    toDollarAmountAcceptZero,
    toSqft,
} from "../utils/operations";

const StyledLb = styled(Link)({
    textDecoration: "none",
});

const StyledPageToolbar = styled("div")({
    justifyContent: "space-between",
    alignItems: "center",
    margin: "20px -8px",
    display: "flex",
    flexWrap: "wrap",
    boxSizing: "border-box",
});

const StyledProgressDiv = styled("div")({
    textAlign: "center",
    width: "5em",
    display: "inline-block",
});

const StyledPaperPadding = styled(Paper)({
    padding: "0 20px 20px",
});

const StyledPaperHeading = styled("h3")({
    fontSize: "18px",
    margin: "14px 0",
});

const StyledHr = styled("hr")({
    margin: "0 -20px 20px -20px",
});

const StyledDefTerm = styled("span")({
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#9E9E9E",
    fontWeight: 600,
});

const StyledDefData = styled("dd")({
    fontSize: "14px",
    color: "#000",
});

const StyledSortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const StyledIdLink = styled(Link)({
    textDecoration: "none",
    color: "#2C99D2",
    fontWeight: 600,
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
});

const StyledGoBackLink = styled("span")({
    textDecoration: "none",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
    },
});

const StyledDeleteButton = styled(Button)({
    backgroundColor: "#F44336",
    color: "white",
    "&:hover": {
        backgroundColor: "#C62828",
    },
});

const DealSummary = (props) => {
    const {
        addVoucherInstallment,
        userProfile,
        columnToSort,
        creator,
        handleSort,
        isLoading,
        isLoadingContent,
        loading,
        sortDirection,
        startVoucher,
        values,
        startDeal,
        handleStartReviewingAsCorporateData,
        handleDownloadDealDocuments,
        role,
        loadDealDocumentList,
        match,
        deleteDeal,
        history,
        location,
    } = props;

    const [openDeleteModal, setOpenDeleteModal] = useState(false);

    useEffect(() => {
        const { id } = match.params;
        loadDealDocumentList(id);
    }, []);
    const handleOpenDeleteModal = () => {
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    const handleDelete = async () => {
        setOpenDeleteModal(false);
        const result = await deleteDeal(values.dealKey);
        if (result.success) {
            history.push(`/deals`);
        }
    };

    const showDocumentList = () => {
        const show =
            includes(["accounting", "evp", "dataAdmin", "dataEntry", "auditor"], userProfile.role) ||
            (includes(["portlandBrokerAdmin"], userProfile.role) &&
                isPropertyInState(get(values, "listing.property", null), "OR")) ||
            (includes(["arizonaBrokerAdmin"], userProfile.role) &&
                isPropertyInState(get(values, "listing.property", null), "AZ")) ||
            userProfile.username === get(values, "deal.auditCreatedBy", null) ||
            isBrokerOnListing(get(values, "listing.listingBrokers", []), userProfile.personKey) ||
            isBrokerOnDeal(get(values, "brokerCommissions", []), userProfile.personKey);

        return show;
    };

    const showVoucherView = () => {
        const show =
            includes(["accounting", "evp", "dataAdmin", "dataEntry"], userProfile.role) ||
            (includes(["portlandBrokerAdmin"], userProfile.role) &&
                isPropertyInState(get(values, "listing.property", null), "OR")) ||
            (includes(["arizonaBrokerAdmin"], userProfile.role) &&
                isPropertyInState(get(values, "listing.property", null), "AZ")) ||
            userProfile.username === get(values, "deal.auditCreatedBy", null) ||
            isBrokerOnListing(get(values, "listing.listingBrokers", []), userProfile.personKey) ||
            isBrokerOnDeal(get(values, "brokerCommissions", []), userProfile.personKey);

        return show;
    };

    const handleAddVoucherInstallment = async (dealKey) => {
        const result = await addVoucherInstallment(dealKey);
        if (result) {
            if (result.voucherKey) {
                history.push(`/voucher/${result.voucherKey}`);
            }
        }
    };

    const handleStartDeal = async (dealKey) => {
        const result = await startDeal(dealKey);
        if (result.success) {
            history.push(`/comp/${dealKey}`);
        }
    };

    const handleStartVoucher = async (dealKey) => {
        const result = await startVoucher(dealKey);
        if (result) {
            if (result.voucherKey) {
                history.push(`/voucher/${result.voucherKey}`);
            }
        }
    };

    const handleStartReviewingAsCorporateDataAndRedirect = async (deal) => {
        const result = await handleStartReviewingAsCorporateData(deal, location.pathname);
        if (result) {
            // This conditional check seems useless, a consulting deal autocloses and doesn't need cdt review
            // A closed comp would no longer need CDT review
            if (deal.transactionType === "Consulting") {
                history.push(`/voucher/${deal.consultingVoucherId}`);
            } else if (deal.compStatus === "Closed") {
                history.push(`/voucher/${result.voucherKey}`);
            } else {
                history.push(`/comp/${deal.dealKey}`);
            }
        }
    };

    const listing = values.listing;
    const dealStatus = values.dealStatus;
    // Wait for API call to retrieve deal data before rendering toolbar and Listing Information section (see dealOperations-getDealSummary)
    return isLoading ? null : (
        <div>
            {/* //toolbar */}
            <StyledPageToolbar>
                <div style={{ padding: "8px", minWidth: 0, boxSizing: "border-box" }}>
                    <StyledGoBackLink onClick={() => history.goBack()}>
                        <LeftChevron />
                    </StyledGoBackLink>
                </div>
                <div style={{ padding: "8px", maxWidth: "100%", boxSizing: "border-box", flexGrow: 1, flexBasis: 0 }}>
                    <h2 className="page-title">
                        {get(listing, "listingName", null) ? get(listing, "listingName", null) : get(values, "dealKey")}
                    </h2>
                    <p className="page-subtitle">
                        {buildAddress(get(values, "listing.property.primaryAddress"))} | {get(values, "propertyType")}{" "}
                        {get(values, "transactionType")}
                    </p>
                </div>
                <div
                    style={{
                        padding: "8px",
                        maxWidth: "33.333333%",
                        boxSizing: "border-box",
                        flexBasis: "33.333333%",
                        flexGrow: 0,
                        alignItems: "flex-end",
                        display: "flex",
                        flexWrap: "wrap",
                    }}
                >
                    <div
                        style={{
                            maxWidth: "50%",
                            boxSizing: "border-box",
                            flexBasis: "50%",
                            flexGrow: 0,
                            alignItems: "center",
                        }}
                    >
                        {(values.auditCreatedBy === userProfile.user ||
                            includes(["dataAdmin", "dataEntry"], userProfile.role)) &&
                            get(values, "vouchers", []).length === 0 &&
                            dealStatus !== "Deleted" && (
                                <StyledDeleteButton
                                    style={{ float: "right" }}
                                    variant="contained"
                                    color="primary"
                                    onClick={handleOpenDeleteModal}
                                >
                                    Delete Deal
                                </StyledDeleteButton>
                            )}
                    </div>
                    <div
                        style={{
                            maxWidth: "50%",
                            boxSizing: "border-box",
                            flexBasis: "50%",
                            flexGrow: 0,
                            textAlign: "right",
                        }}
                    >
                        <p className="page-subtitle text-right">Total Consideration</p>
                        <h2 className="page-title text-right">
                            {utils.operations.toDollarAmount(get(values, "totalConsiderationAmount", 0))}
                        </h2>
                    </div>
                </div>
            </StyledPageToolbar>

            {/* //heading */}
            <StyledPaperPadding>
                <div style={{ display: "flex", alignItems: "flex-start", margin: "-8px" }}>
                    <div style={{ padding: "8px" }}>
                        <StyledPaperHeading>Listing Information</StyledPaperHeading>
                    </div>
                </div>
                <StyledHr />
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-evenly",
                        flexWrap: "wrap",
                        margin: "-8px",
                        boxSizing: "border-box",
                    }}
                >
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Date Avail</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{values.dateAvailable}</StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Total SF</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{get(values, "propertyAttributes.totalBldgSf")}</StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>
                            {get(listing, "listingType", "") === "Sale" ? "List Price" : "Rate"}
                        </StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>
                            {get(listing, "listingType", "") === "Sale"
                                ? `${
                                      !isNaN(get(listing, "listPrice", "-"))
                                          ? numeral(get(listing, "listPrice", 0)).format("0,0.00")
                                          : ""
                                  }`
                                : `${
                                      !isNaN(get(listing, "askingRentMax", "-"))
                                          ? numeral(get(listing, "askingRentMax", 0)).format("0,0.00")
                                          : ""
                                  }`}
                        </StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Floor</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{get(listing, "attributes.numFloors", "")}</StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Broker</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>
                            {aggregateBrokers(get(listing, "broker", []), "brokerAgentKey.person")}
                        </StyledDefData>
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-evenly",
                        flexWrap: "wrap",
                        margin: "-8px",
                        boxSizing: "border-box",
                    }}
                >
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Type</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{values.transactionType}</StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>
                            {get(listing, "listingType", "") === "Sale" ? "Sale SF" : "SF Avail"}
                        </StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>
                            {get(listing, "listingType", "") === "Sale"
                                ? toSqft(get(listing, "sfAvail", 0))
                                : minMaxSqftConcat(get(listing, "sfDivisibleTo", 0), get(listing, "sfAvail", 0))}
                        </StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>NNN Rate</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{values.expenseNnnOpAmount}</StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Sublet</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{booleanToYN(values.subleaseFlg)}</StyledDefData>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefTerm>Created By</StyledDefTerm>
                    </div>
                    <div
                        style={{ flexGrow: 1, flexBasis: 0, padding: "8px", boxSizing: "border-box", maxWidth: "100%" }}
                    >
                        <StyledDefData>{typeof creator === "string" ? creator.toString() : "N/A"}</StyledDefData>
                    </div>
                </div>
            </StyledPaperPadding>

            {/* comp */}
            {
                role !== "auditor" ? (
                    get(values, "compStatus") ? ( //startA * * * * *
                        <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    margin: "-12px",
                                    boxSizing: "border-box",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        maxwidth: "41.666667%",
                                        flexBasis: "41.666667%",
                                        flexGrow: 0,
                                        boxSizing: "border-box",
                                        margin: "-8px",
                                        padding: "12px",
                                    }}
                                >
                                    <div style={{ padding: "8px" }}>
                                        {values.compStatus === "Closed" ? (
                                            <StepperIconComplete />
                                        ) : (
                                            <StepperIcon text="1" fill="#2196f3" />
                                        )}
                                    </div>
                                    <div style={{ padding: "8px" }}>
                                        <h4>Comp / RE Compliance</h4>
                                    </div>
                                    <div style={{ padding: "8px" }}>
                                        {values.compStatus && (
                                            <StatusChip
                                                status={
                                                    includes(
                                                        [
                                                            "AZ Termination Review Required",
                                                            "Portland Termination Review Required",
                                                        ],
                                                        values.compStatus
                                                    )
                                                        ? "Termination Review Required"
                                                        : includes(
                                                              ["AZ Terminated", "Portland Terminated"],
                                                              values.compStatus
                                                          )
                                                        ? "Terminated"
                                                        : values.compStatus
                                                }
                                            />
                                        )}
                                    </div>
                                </div>
                                <div style={{ padding: "12px", boxSizing: "border-box" }}>
                                    {(get(values, "compStatus") === null || get(values, "compStatus") === undefined) &&
                                    get(userProfile, "role", null) === "broker" ? (
                                        <StyledLb to={`/comp/${values.dealKey}`}>
                                            <Button variant="contained" color="primary">
                                                Start
                                            </Button>
                                        </StyledLb>
                                    ) : get(compAction, `${userProfile.role}.${values.compStatus}.buttonText`, null) ===
                                      "Start" ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleStartReviewingAsCorporateDataAndRedirect(values)}
                                        >
                                            Start
                                        </Button>
                                    ) : get(compAction, `${userProfile.role}.${values.compStatus}`, null) ? (
                                        <StyledLb to={`/comp/${values.dealKey}`}>
                                            <Button
                                                variant={get(
                                                    compAction,
                                                    `${userProfile.role}.${values.compStatus}.variant`,
                                                    null
                                                )}
                                                color="primary"
                                            >
                                                {get(
                                                    compAction,
                                                    `${userProfile.role}.${values.compStatus}.buttonText`,
                                                    null
                                                )}
                                            </Button>
                                        </StyledLb>
                                    ) : (
                                        <StyledLb to={`/comp/${values.dealKey}`}>
                                            <Button variant="outlined" color="primary">
                                                View
                                            </Button>
                                        </StyledLb>
                                    )}
                                </div>
                            </div>
                        </Paper>
                    ) : (
                        <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    margin: "-12px",
                                    boxSizing: "border-box",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                        maxwidth: "41.666667%",
                                        flexBasis: "41.666667%",
                                        flexGrow: 0,
                                        boxSizing: "border-box",
                                        margin: "-8px",
                                        padding: "12px",
                                    }}
                                >
                                    <div style={{ padding: "8px" }}>
                                        <StepperIcon text="1" fill="#2196f3" />
                                    </div>
                                    <div style={{ flexGrow: 1, flexBasis: 0, padding: "8px" }}>
                                        <h4>Comp / RE Compliance</h4>
                                    </div>
                                </div>
                                <div style={{ padding: "12px" }}>
                                    {userProfile.role === "broker" ? (
                                        <Button
                                            variant="contained"
                                            onClick={() => handleStartDeal(values.dealKey)}
                                            color="primary"
                                        >
                                            Start
                                        </Button>
                                    ) : get(compAction, `${userProfile.role}.${values.compStatus}`, null) ? (
                                        <StyledLb to={`/comp/${values.dealKey}`}>
                                            <Button
                                                variant={get(
                                                    compAction,
                                                    `${userProfile.role}.${values.compStatus}.variant`,
                                                    null
                                                )}
                                                color="primary"
                                            >
                                                {get(
                                                    compAction,
                                                    `${userProfile.role}.${values.compStatus}.buttonText`,
                                                    null
                                                )}
                                            </Button>
                                        </StyledLb>
                                    ) : (
                                        <StyledLb to={`/comp/${values.dealKey}`}>
                                            <Button variant="outlined" color="primary">
                                                View
                                            </Button>
                                        </StyledLb>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: "flex", padding: "0 12px 12px 12px" }}>
                                <div>
                                    <p>
                                        Complete your comp information here and it will appear in your comps section. As
                                        a company, we will use this information to better compete in the marketplace.
                                    </p>
                                </div>
                            </div>
                        </Paper>
                    )
                ) : null
                //endA * * * * *
            }

            {/* voucher */}
            {role !== "auditor" ? (
                get(values, "compStatus", null) !== "Draft" && get(values, "compStatus", null) !== null ? (
                    <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                        <div
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifyContent: "space-between",
                                margin: "-12px",
                                boxSizing: "border-box",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    maxwidth: "41.666667%",
                                    flexBasis: "41.666667%",
                                    flexGrow: 0,
                                    boxSizing: "border-box",
                                    margin: "-8px",
                                    padding: "12px",
                                }}
                            >
                                <div style={{ padding: "8px", boxSizing: "border-box" }}>
                                    {values.vouchers &&
                                    values.vouchers.length > 0 &&
                                    values.vouchers[values.vouchers.length - 1].voucherStatus === "Closed" ? (
                                        <StepperIconComplete />
                                    ) : (
                                        <StepperIcon text="2" fill="#2196f3" />
                                    )}
                                </div>
                                <div style={{ padding: "8px" }}>
                                    <h4>Voucher</h4>
                                </div>
                            </div>

                            <div style={{ padding: "12px", boxSizing: "border-box" }}>
                                {includes(
                                    ["broker", "evp", "portlandBrokerAdmin", "arizonaBrokerAdmin"],
                                    userProfile.role
                                ) &&
                                (values.totalInstallments === 1 || values.totalInstallments === null) &&
                                // Wait for API call to retrieve voucher data before rendering voucher section (see dealOperations-getDealSummary)
                                !isLoadingContent ? (
                                    get(values, "vouchers", []).length > 0 ? null : (
                                        <Button
                                            disabled={
                                                userProfile.user === get(values, "auditCreatedBy", null) ? false : true
                                            }
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleStartVoucher(values.dealKey)}
                                        >
                                            Start
                                        </Button>
                                    )
                                ) : isLoadingContent ? null : (
                                    includes(
                                        ["broker", "evp", "portlandBrokerAdmin", "arizonaBrokerAdmin"],
                                        userProfile.role
                                    ) && (
                                        <React.Fragment>
                                            {loading.addVoucherInstallment ? (
                                                <StyledProgressDiv>
                                                    <CircularProgress size={20} color="primary" />
                                                </StyledProgressDiv>
                                            ) : (
                                                <Button
                                                    onClick={() => handleAddVoucherInstallment(values.dealKey)}
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={
                                                        isLoadingContent ||
                                                        get(values, "vouchers", []).length >=
                                                            get(values, "totalInstallments")
                                                    }
                                                >
                                                    <Add fill="#ffffff" />
                                                    Add Installment
                                                </Button>
                                            )}
                                        </React.Fragment>
                                    )
                                )}
                            </div>
                        </div>
                        {get(values, "vouchers", []).length > 0 || isLoadingContent ? null : (
                            <div style={{ display: "flex", padding: "0 12px 12px 12px" }}>
                                <div>
                                    <p>
                                        Fill out your voucher information once you receive payment or proof of payment
                                        so you can get paid in a timely fashion. Vouchers submitted by noon on Tuesday
                                        are paid out the same week.
                                    </p>
                                </div>
                            </div>
                        )}
                        {isLoadingContent
                            ? null
                            : showVoucherView() &&
                              values.vouchers &&
                              values.vouchers.length > 0 && (
                                  <div style={{ display: "flex" }}>
                                      <div style={{ flexGrow: 1, flexBasis: 0 }}>
                                          <Table>
                                              <TableHead>
                                                  <TableRow>
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                              }}
                                                              onClick={() => handleSort("voucherKey")}
                                                          >
                                                              <span>Voucher</span>
                                                          </div>
                                                      </TableCell>
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                              }}
                                                              onClick={() => handleSort("installmentNum")}
                                                          >
                                                              <StyledSortable>Installment</StyledSortable>
                                                              <Sorty />
                                                              {/* this table header is sortable */}
                                                          </div>
                                                      </TableCell>
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                              }}
                                                              onClick={() => handleSort("datePaid")}
                                                          >
                                                              <span>Payment Date</span>
                                                          </div>
                                                      </TableCell>
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                              }}
                                                              onClick={() => handleSort("myAmount")}
                                                          >
                                                              <StyledSortable>My Amount</StyledSortable>
                                                              <UpChevron />
                                                          </div>
                                                      </TableCell>
                                                      <TableCell>
                                                          <div
                                                              style={{
                                                                  display: "flex",
                                                                  alignItems: "center",
                                                              }}
                                                              onClick={() => handleSort("voucherStatus")}
                                                          >
                                                              <StyledSortable>Status</StyledSortable>
                                                              <DownChevron />
                                                          </div>
                                                      </TableCell>
                                                      <TableCell align="right"></TableCell>
                                                  </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                  {values.vouchers &&
                                                      orderBy(values.vouchers, columnToSort, sortDirection).map(
                                                          (x, i) => (
                                                              <TableRow key={`tr-${i}`} selectable="false">
                                                                  <TableCell>
                                                                      <StyledIdLink to={`/voucher/${x.voucherKey}`}>
                                                                          {get(x, "voucherKey")}
                                                                      </StyledIdLink>
                                                                  </TableCell>
                                                                  <TableCell>
                                                                      {!values.totalInstallments ||
                                                                      get(values, "totalInstallments", 1) === 1
                                                                          ? "1 of 1"
                                                                          : `${get(x, "installmentNum", "")} of ${
                                                                                values.totalInstallments
                                                                            }`}
                                                                  </TableCell>
                                                                  <TableCell>
                                                                      {get(
                                                                          get(values, "installments", [{}])[
                                                                              get(x, "installmentNum", 1) - 1
                                                                          ],
                                                                          "dueDate",
                                                                          ""
                                                                      )}
                                                                  </TableCell>
                                                                  <TableCell>
                                                                      {toDollarAmountAcceptZero(
                                                                          get(
                                                                              values,
                                                                              `myAmounts.${x.voucherKey}.myAmount`
                                                                          )
                                                                      )}
                                                                  </TableCell>
                                                                  <TableCell>
                                                                      <StatusChip status={get(x, "voucherStatus")} />
                                                                  </TableCell>
                                                                  <TableCell
                                                                      style={{
                                                                          textAlign: "right",
                                                                          paddingRight: "0px",
                                                                      }}
                                                                  >
                                                                      {get(
                                                                          compAction,
                                                                          `${userProfile.role}.${x.voucherStatus}.buttonText`,
                                                                          null
                                                                      ) === "Start" ? (
                                                                          <Button
                                                                              variant="contained"
                                                                              color="primary"
                                                                              onClick={() =>
                                                                                  handleStartReviewingAsCorporateDataAndRedirect(
                                                                                      values
                                                                                  )
                                                                              }
                                                                          >
                                                                              Start
                                                                          </Button>
                                                                      ) : get(
                                                                            voucherAction,
                                                                            `${userProfile.role}.${x.voucherStatus}`,
                                                                            null
                                                                        ) ? (
                                                                          <StyledLb to={`/voucher/${x.voucherKey}`}>
                                                                              <Button
                                                                                  variant={get(
                                                                                      voucherAction,
                                                                                      `${userProfile.role}.${x.voucherStatus}.variant`,
                                                                                      null
                                                                                  )}
                                                                                  color="primary"
                                                                              >
                                                                                  {get(
                                                                                      compAction,
                                                                                      `${userProfile.role}.${x.voucherStatus}.buttonText`,
                                                                                      null
                                                                                  )}
                                                                              </Button>
                                                                          </StyledLb>
                                                                      ) : (
                                                                          <StyledLb to={`/voucher/${x.voucherKey}`}>
                                                                              <Button
                                                                                  variant="outlined"
                                                                                  color="primary"
                                                                              >
                                                                                  View
                                                                              </Button>
                                                                          </StyledLb>
                                                                      )}
                                                                  </TableCell>
                                                              </TableRow>
                                                          )
                                                      )}
                                              </TableBody>
                                          </Table>
                                      </div>
                                  </div>
                              )}
                    </Paper>
                ) : (
                    // no comp status
                    <div>
                        <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                            <div style={{ display: "flex", alignItems: "center", margin: "-8px" }}>
                                <div style={{ padding: "8px" }}>
                                    <StepperIcon text="2" fill="#cccccc" />
                                </div>
                                <div style={{ padding: "8px" }}>
                                    <h4>Voucher</h4>
                                </div>
                            </div>
                            <div style={{ display: "flex", padding: "0 12px 12px 12px" }}>
                                <div>
                                    <p>
                                        Fill out your voucher information once you receive payment or proof of payment
                                        so you can get paid in a timely fashion. Vouchers submitted by noon on Tuesday
                                        are paid out the same week.
                                    </p>
                                </div>
                            </div>
                        </Paper>
                    </div>
                )
            ) : null}

            <Dialog open={openDeleteModal} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogContent>Are you sure you want to permanently delete this deal?</DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteModal} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="primary" variant="contained">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            {showDocumentList() && (
                <Paper style={{ marginTop: "2em", padding: "0 20px" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            margin: "-12px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                width: "41.666667%",
                                margin: "-8px",
                                padding: "12px",
                            }}
                        >
                            <div style={{ padding: "8px" }}>
                                <h4>Documents</h4>
                            </div>
                        </div>
                        <div style={{ padding: "12px" }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => handleDownloadDealDocuments(get(values, "dealKey"))}
                            >
                                Download All
                            </Button>
                        </div>
                    </div>
                    <DocumentDrop
                        type="deal"
                        documentTypes="all"
                        viewOnly={true}
                        includeSource={true}
                        downloadView={true}
                        role={role}
                    />
                </Paper>
            )}
        </div>
    );
};

const compAction = {
    broker: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
    },
    evp: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
    },
    portlandBrokerAdmin: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Portland Review Required": {
            buttonText: "Review",
            action: () => {},
            variant: "contained",
        },
    },
    arizonaBrokerAdmin: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "AZ Review Required": {
            buttonText: "Review",
            action: () => {},
            variant: "contained",
        },
    },
    dataEntry: {
        "Needs Review": {
            buttonText: "Start",
            action: () => {},
            variant: "contained",
        },
        "In Review": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Admin Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        Closed: {
            buttonText: "View",
            action: () => {},
            variant: "outlined",
        },
    },
    dataAdmin: {
        "Final Review": {
            buttonText: "Review",
            action: () => {},
            variant: "contained",
        },
    },
};

const voucherAction = {
    broker: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
    },
    evp: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
    },
    portlandBrokerAdmin: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Portland Review Required": {
            buttonText: "Review",
            action: () => {},
            variant: "contained",
        },
    },
    arizonaBrokerAdmin: {
        Draft: {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Broker Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "AZ Review Required": {
            buttonText: "Review",
            action: () => {},
            variant: "contained",
        },
    },
    dataEntry: {
        "Needs Review": {
            buttonText: "Start",
            action: () => {},
            variant: "contained",
        },
        "In Review": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        "Admin Action Required": {
            buttonText: "Edit",
            action: () => {},
            variant: "contained",
        },
        Closed: {
            buttonText: "View",
            action: () => {},
            variant: "outlined",
        },
    },
    dataAdmin: {
        "Final Review": {
            buttonText: "Review",
            action: () => {},
            variant: "contained",
        },
    },
};

export default DealSummary;
